import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { forwardRef, useState } from "react";

import { Input, type InputProps } from "./input";

export type PasswordInputProps = {
  /** Controls whether to render the show/hide button (default: true). */
  showToggle?: boolean;
  /** Optional accessible label override for the toggle button. */
  toggleAriaLabelShow?: string;
  toggleAriaLabelHide?: string;
} & Omit<InputProps, "type">;

/**
 * Production-grade password field with built-in show/hide toggle.
 *
 * - Mirrors the `<Input>` API (className, hasError, ref, all native input props).
 * - Toggle button is keyboard accessible and announces aria-pressed state.
 * - When the input is shown the icon is a plain `Eye` (no slash);
 *   when hidden the icon is `EyeSlash` so users can see what action a click takes.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      showToggle = true,
      toggleAriaLabelShow = "Show password",
      toggleAriaLabelHide = "Hide password",
      ...props
    },
    ref,
  ) => {
    const [shown, setShown] = useState(false);

    if (!showToggle) {
      return <Input ref={ref} type="password" className={className} {...props} />;
    }

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={shown ? "text" : "password"}
          className={cn("pr-10", className)}
          {...props}
        />
        <button
          type="button"
          aria-label={shown ? toggleAriaLabelHide : toggleAriaLabelShow}
          aria-pressed={shown}
          tabIndex={0}
          className="absolute inset-y-0 right-2 my-auto flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => {
            setShown((v) => !v);
          }}
        >
          {shown ? (
            <EyeIcon size={16} weight="regular" />
          ) : (
            <EyeSlashIcon size={16} weight="regular" />
          )}
        </button>
      </div>
    );
  },
);

PasswordInput.displayName = "PasswordInput";
